import React, { Component } from 'react';
import ReactQuill from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill/dist/quill.snow.css';
import { uploadToZOS, message } from '@/utils/tools';

// 注册图片调整大小模块
ReactQuill.Quill.register('modules/imageResize', ImageResize);

// 注册对齐样式为内联 style（而非 class）
const AlignStyle = ReactQuill.Quill.import('attributors/style/align');
AlignStyle.whitelist = ['right', 'center', 'justify'];
ReactQuill.Quill.register(AlignStyle, true);

export default class TextEditor extends Component {
    constructor(props) {
        super(props);
        
        // 配置精简的工具栏（只保留纯文本格式+图片）
        this.modules = {
            toolbar: {
                container: [
                    [{ 'header': [1, 2, 3, false] }],  // 标题
                    ['bold', 'italic', 'underline'],    // 粗体、斜体、下划线
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],  // 列表
                    [{ 'align': [] }],                  // 对齐方式（左中右）
                    ['image'],                          // 图片
                    ['clean']                           // 清除格式
                ],
                handlers: {
                    'image': this.imageHandler
                }
            },
            imageResize: {
                displayStyles: {
                    backgroundColor: 'black',
                    border: 'none',
                    color: 'white'
                },
                modules: ['Resize', 'DisplaySize']
            },
        };

        this.formats = [
            'header',
            'bold', 'italic', 'underline',
            'list', 'bullet',
            'align',
            'image'
        ];
    }

    componentDidMount() {
        // 延迟一帧确保 ReactQuill 完全初始化
        setTimeout(() => {
            const quill = this.quillRef?.getEditor();
            if (quill) {
                quill.root.addEventListener('paste', this.handlePaste);
            }
        }, 0);
    }

    componentWillUnmount() {
        const quill = this.quillRef?.getEditor();
        if (quill) {
            quill.root.removeEventListener('paste', this.handlePaste);
        }
    }

    // 处理粘贴事件：拦截图片上传到天翼云
    handlePaste = (e) => {
        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        // 查找剪贴板中的图片
        const items = clipboardData.items;
        const imageItems = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                imageItems.push(items[i]);
            }
        }

        if (imageItems.length === 0) return; // 没有图片，正常粘贴

        // 阻止默认粘贴行为（避免插入 base64）
        e.preventDefault();

        // 如果有文本内容，先手动插入文本
        const text = clipboardData.getData('text/plain');
        const quill = this.quillRef.getEditor();
        const range = quill.getSelection(true);
        if (text) {
            quill.insertText(range.index, text);
        }

        // 上传图片到天翼云 ZOS
        imageItems.forEach(item => {
            const file = item.getAsFile();
            if (!file) return;

            // 验证文件大小（限制 10MB）
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                message('粘贴的图片大小不能超过10MB', 'error');
                return;
            }

            uploadToZOS(file, 'report/images')
                .then(imageUrl => {
                    const q = this.quillRef.getEditor();
                    const r = q.getSelection(true);
                    q.insertEmbed(r.index, 'image', imageUrl);
                    q.setSelection(r.index + 1);
                    message('图片上传成功', 'success');
                })
                .catch(error => {
                    console.error('粘贴图片上传失败:', error);
                    message('图片上传失败，请重试', 'error');
                });
        });
    };

    // 自定义图片上传处理 - 天翼云 ZOS
    imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = () => {
            const file = input.files[0];
            if (!file) return;

            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                message('请上传图片文件', 'error');
                return;
            }

            // 验证文件大小（限制 10MB）
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                message('图片大小不能超过10MB', 'error');
                return;
            }

            // 调用天翼云 ZOS 上传接口
            uploadToZOS(file, 'report/images')
                .then(imageUrl => {
                    const quill = this.quillRef.getEditor();
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', imageUrl);
                    message('图片上传成功', 'success');
                })
                .catch(error => {
                    console.error('图片上传失败:', error);
                    message('图片上传失败，请重试', 'error');
                });
        };
    };

    // 将内联样式转换为 Quill 的 align class（用于加载后端数据）
    convertInlineStyleToAlign = (html) => {
        if (!html) return html;
        
        const div = document.createElement('div');
        div.innerHTML = html;
        
        // 查找所有带 text-align 样式的元素
        const elements = div.querySelectorAll('[style*="text-align"]');
        elements.forEach(el => {
            const textAlign = el.style.textAlign;
            if (textAlign && textAlign !== 'left' && textAlign !== '') {
                el.classList.add('ql-align-' + textAlign);
                el.style.textAlign = ''; // 移除内联样式
                if (!el.getAttribute('style')) {
                    el.removeAttribute('style');
                }
            }
        });
        
        return div.innerHTML;
    };

    // 将 Quill 的 align class 转换为内联样式（用于后端生成 HTML）
    convertAlignToInlineStyle = (html) => {
        if (!html) return html;
        
        // 创建临时 DOM 元素
        const div = document.createElement('div');
        div.innerHTML = html;
        
        // 查找所有带对齐 class 的元素
        const alignClasses = {
            'ql-align-center': 'center',
            'ql-align-right': 'right',
            'ql-align-justify': 'justify',
            'ql-align-left': 'left'
        };
        
        Object.keys(alignClasses).forEach(className => {
            const elements = div.querySelectorAll('.' + className);
            elements.forEach(el => {
                el.style.textAlign = alignClasses[className];
                el.classList.remove(className);
            });
        });
        
        return div.innerHTML;
    };

    handleChange = (value) => {
        if (this.props.onChange) {
            // 转换对齐样式为内联样式
            const convertedValue = this.convertAlignToInlineStyle(value);
            this.props.onChange(convertedValue);
        }
    };

    handleBlur = () => {
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    componentDidUpdate(prevProps) {
        // 只在初始加载时同步一次数据（避免覆盖用户编辑）
        if (!prevProps.value && this.props.value) {
            const quill = this.quillRef?.getEditor();
            if (quill) {
                // 将后端保存的内联样式转换为 Quill 的 class
                const convertedValue = this.convertInlineStyleToAlign(this.props.value);
                if (quill.root.innerHTML !== convertedValue) {
                    quill.root.innerHTML = convertedValue;
                }
            }
        }
    }

    render() {
        const { width = '100%', height, name } = this.props;
        
        return (
            <div 
                name={name}
                style={{ width }}
            >
                <ReactQuill
                    ref={(el) => { this.quillRef = el; }}
                    theme="snow"
                    defaultValue={this.props.value}  // 使用 defaultValue 而非 value，避免受控组件覆盖
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    modules={this.modules}
                    formats={this.formats}
                    placeholder="请输入内容..."
                />
            </div>
        );
    }
}
